import React, { memo, useEffect } from 'react'
import { Button } from 'transfix-ui/components/Button'
import { Card } from 'transfix-ui/components/Card'
import { InlineEditContainer } from 'transfix-ui/components/InlineEditContainer'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { LockedTooltip } from 'apps/fleet-planner/components/load-details/LockedTooltip'
import { IStop } from 'apps/fleet-planner/types/loads'
import { LOAD_FIELDS } from 'apps/fleet-planner/utils/permissions'
import { ITimeFormats } from 'apps/fleet-planner/utils/userDateTimeSettings'
import { Appointment, APPOINTMENT_VARIANT } from './Appointment'
import { formatDateTime } from './utils'

interface IAppointmentDetailsProps {
  startAt: IStop['startAt']
  endAt: IStop['endAt']
  displayTimezone: IStop['displayTimezone']
  stopType: IStop['stopType']
  sequence: number
  timeFormats: ITimeFormats
}

export const AppointmentDetails = memo<IAppointmentDetailsProps>(
  ({ displayTimezone, endAt, sequence, startAt, stopType, timeFormats }) => {
    return (
      <span data-test={`appointment-time-${stopType}-${sequence}`}>
        {formatDateTime(timeFormats, startAt, endAt, displayTimezone)}
      </span>
    )
  }
)

interface IAppointmentInlineEdit {
  stop: IStop
  currentEditField?: string
  onEditModeRequested(name?: string): void
  onSubmit: () => void
  onCancel: () => void
  onStopUpdate: (stop: Partial<IStop>) => void
  stopIndex: number
  loading?: boolean
  error?: string
  timeFormats: ITimeFormats
  isLocked?: boolean
}

export const AppointmentInlineEdit = memo<IAppointmentInlineEdit>(
  ({
    currentEditField,
    error,
    isLocked,
    loading,
    onCancel,
    onEditModeRequested,
    onStopUpdate,
    onSubmit,
    stop,
    stopIndex,
    timeFormats,
  }) => {
    const { displayTimezone, endAt, id, startAt, stopSequence, stopType } = stop
    const NAME = `${LOAD_FIELDS.appointment}-${id}`
    const isEditMode = currentEditField === NAME

    useEffect(() => {
      if (!error && !loading && isEditMode) onEditModeRequested('')
    }, [error, loading, onEditModeRequested])

    return (
      <InlineEditContainer
        isLoading={loading}
        isLocked={isLocked}
        lockedIcon={<LockedTooltip className='locked-icon' />}
        dataTest={`load-details-stop-appointment-${stopType}-${stopSequence}`}
        name={NAME}
        editModeEnabled={isEditMode}
        resetOnClickOutside={false}
        editMode={
          <Card className='pa4'>
            {error && (
              <div className='row pl0 mb4'>
                <InlineMessage message={error} type={INLINE_MESSAGE_VARIANTS.ALERT} />
              </div>
            )}
            <div data-test={`inline-edit-appointment-${stop.stopType}-${stop.stopSequence}`}>
              <Appointment
                timeFormat={timeFormats.stopTimeInput}
                id='inline-edit'
                stopIndex={stopIndex}
                stop={stop}
                onStopChange={onStopUpdate}
                variant={APPOINTMENT_VARIANT.LOAD_DETAILS}
              />
              <div className='tr'>
                <div className='mt4'>
                  <Button color='tertiary' size='large' onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button
                    color='primary'
                    size='large'
                    onClick={onSubmit}
                    className='ml2'
                    data-test={`appointment-submit-${stopType}-${stopSequence}`}>
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        }
        onEditModeRequested={onEditModeRequested}>
        <AppointmentDetails
          timeFormats={timeFormats}
          displayTimezone={displayTimezone}
          endAt={endAt}
          startAt={startAt}
          stopType={stopType}
          sequence={stopSequence}
        />
      </InlineEditContainer>
    )
  }
)
